import { ConditionSchema, SerializableSchema, z } from "@repo/utils";
import { EntityTypeSchema } from "../datasync/z.js";
import { defaultCustomerFields, defaultPositionFields } from "../../server/values.js";
import { ClassificationSchema, DocumentSystemSchema, EmailSystemSchema, ErpSystemSchema } from "../z.js";
import { OrganizationIdSchema } from "../../organization/z.js";
import { masterDataSources } from "./values.js";
import { InboxIdSchema } from "../../inbox/z.js";

export const OdooAuthConfigSchema = z.object({
    configType: z.literal("ODOO"),
    url: z.string().check(z.url()),
    username: z.string(),
    password: z.string(),
    database: z.string(),
    companyIds: z.nullish(z.array(z.number())),
});

export const ReflexPIMAuthConfigSchema = z.object({
    configType: z.literal("REFLEX_PIM"),
    tokenUrl: z.string(),
    baseUrl: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
});

export const HaufeX360AuthConfigSchema = z.object({
    configType: z.literal("HAUFE_X360"),
    baseUrl: z.string(),
    username: z.string(),
    password: z.string(),
    tenant: z.string(),
    branch: z.string(),
    endpointPath: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
});

export const DresselhausSAPAuthConfigSchema = z.object({
    configType: z.literal("DRESSELHAUS_SAP"),
    baseUrl: z.string(),
    username: z.string(),
    password: z.string(),
});

export const EpicorAuthConfigSchema = z.object({
    configType: z.literal("EPICOR"),
    baseUrl: z.string(),
    username: z.string(),
    password: z.string(),
});

export const OrderwiseAuthConfigSchema = z.object({
    configType: z.literal("ORDERWISE"),
    baseUrl: z.string(),
    username: z.string(),
    password: z.string(),
});

export const FileExportAuthConfigSchema = z.object({
    configType: z.literal("FILE_EXPORT"),
    exportConfig: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("SFTP"),
            host: z.string(),
            basePath: z.optional(z.string()),
            username: z.string(),
            password: z.optional(z.string()),
            privateKey: z.optional(z.string()),
        }),
    ]),
});

export const SapB1AuthConfigSchema = z.object({
    configType: z.literal("SAP_B1"),
    url: z.string(),
    username: z.string(),
    password: z.string(),
    companyDb: z.string(),
});

export const FormDataAuthConfigSchema = z.object({
    configType: z.literal("FORM_DATA"),
    url: z.string(),
    query: z.optional(z.record(z.string(), z.string())),
    params: z.record(z.string(), z.string()),
    auth: z.discriminatedUnion("type", [
        z.object({
            type: z.literal("BASIC"),
            username: z.string(),
            password: z.string(),
        }),
        z.object({
            type: z.literal("OAUTH2_CLIENT_CREDENTIALS"),
            tokenUrl: z.string(),
            clientId: z.string(),
            clientSecret: z.string(),
            scope: z.string(),
        }),
    ]),
});

export const NetsuiteAuthConfigSchema = z.object({
    configType: z.literal("NETSUITE"),
    baseUrl: z.string(),
    consumerKey: z.string(),
    consumerSecret: z.string(),
    accessToken: z.string(),
    tokenSecret: z.string(),
    realm: z.string(),
});

export const MSSQLAuthConfigSchema = z.object({
    configType: z.literal("MSSQL"),
    host: z.string(),
    username: z.string(),
    password: z.string(),
    database: z.string(),
    port: z.number(),
    branches: z.optional(
        z.array(
            z.object({
                branchKey: z.string(),
                dataSetId: z.string(),
            }),
        ),
    ),
});

export const ReflexSAPAuthConfigSchema = z.object({
    configType: z.literal("REFLEX_SAP"),
    host: z.string(),
    ip: z.string(),
    port: z.number(),
});

export const WashtecSAPAuthConfigSchema = z.object({
    configType: z.literal("WASHTEC_SAP"),
    tokenUrl: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
    scope: z.string(),
});

export const NoneAuthConfigSchema = z.object({
    configType: z._default(z.literal("NONE"), "NONE"),
});

export const SchellingAuthConfigSchema = z.object({
    configType: z.literal("SCHELLING"),
    url: z.string(),
    username: z.string(),
    password: z.string(),
});

export const SucoAuthConfigSchema = z.object({
    configType: z.literal("SUCO"),
    url: z.string(),
    db: z.string(),
    username: z.string(),
    password: z.string(),
});

export const GehrAuthConfigSchema = z.object({
    configType: z.literal("GEHR"),
    url: z.string(),
    db: z.string(),
    username: z.string(),
    password: z.string(),
});

export const UtzAuthConfigSchema = z.object({
    configType: z.literal("UTZ"),
    tokenUrl: z.string(),
    baseUrl: z.string(),
    clientId: z.string(),
    clientSecret: z.string(),
});

export const AsatexAuthConfigSchema = z.object({
    configType: z.literal("ASATEX"),
    url: z.string(),
    username: z.string(),
    password: z.string(),
});

export const DocuWareAuthConfigSchema = z.object({
    configType: z.literal("DOCUWARE"),
    platformUrl: z.string(),
    tokenUrl: z.string(),
    username: z.string(),
    password: z.string(),
    fileCabinetId: z.string(),
});

export const AuthConfigSchema = z.discriminatedUnion("configType", [
    NoneAuthConfigSchema,
    OdooAuthConfigSchema,
    DresselhausSAPAuthConfigSchema,
    FileExportAuthConfigSchema,
    SapB1AuthConfigSchema,
    NetsuiteAuthConfigSchema,
    ReflexPIMAuthConfigSchema,
    EpicorAuthConfigSchema,
    OrderwiseAuthConfigSchema,
    MSSQLAuthConfigSchema,
    ReflexSAPAuthConfigSchema,
    HaufeX360AuthConfigSchema,
    SchellingAuthConfigSchema,
    WashtecSAPAuthConfigSchema,
    SucoAuthConfigSchema,
    GehrAuthConfigSchema,
    UtzAuthConfigSchema,
    AsatexAuthConfigSchema,
    DocuWareAuthConfigSchema,
    FormDataAuthConfigSchema,
]);

export const DocumentSystemAuthConfigSchema = z.discriminatedUnion("configType", [
    NoneAuthConfigSchema,
    OdooAuthConfigSchema,
    FileExportAuthConfigSchema,
    SapB1AuthConfigSchema,
    UtzAuthConfigSchema,
    DocuWareAuthConfigSchema,
    FormDataAuthConfigSchema,
]);

export const EmailSystemAuthConfigSchema = z.discriminatedUnion("configType", [
    NoneAuthConfigSchema,
    OdooAuthConfigSchema,
    FileExportAuthConfigSchema,
    SapB1AuthConfigSchema,
    UtzAuthConfigSchema,
    DocuWareAuthConfigSchema,
    FormDataAuthConfigSchema,
]);

export const SalesOrderConfigInboxLabels = z.object({
    pipelineStarted: z.optional(z.string()),
    pipelineFinished: z.optional(z.string()),
    pipelineError: z.optional(z.string()),
    submittedSuccessfully: z.optional(z.string()),
    submittedWithError: z.optional(z.string()),
    ORDER: z.optional(z.string()),
    REQUEST_FOR_QUOTATION: z.optional(z.string()),
});

export const VisibleConditionallySchema = z.object({
    dependentKey: z.string(),
    condition: z.discriminatedUnion("type", [
        z.object({ type: z.literal("EQUALS"), value: z.string() }),
        z.object({ type: z.literal("EXISTS") }),
        z.object({ type: z.literal("NOT_EXISTS") }),
    ]),
});

export const ClassificationConfigEntrySchema = z.object({
    name: z.string(),
    description: z.optional(z.string()),
    pipeline: z.optional(z.string()),
});

export const ExtractedCustomFieldSchema = z.object({
    technicalName: z.string(),
    readableNameEnglish: z.string(),
    readableNameGerman: z.string(),
    type: z.enum(["string", "number", "money", "date"]),
    array: z._default(z.boolean(), false),
    nullable: z._default(z.boolean(), true),
    visible: z._default(z.boolean(), true),
    visibleWhenEmpty: z._default(z.boolean(), true),
    visibleConditionally: z.optional(VisibleConditionallySchema),
    enum: z.optional(
        z.discriminatedUnion("type", [
            z.object({
                type: z.literal("FIXED"),
                enum: z.array(z.string()),
                labels: z.optional(z.record(z.string(), z.string())),
            }),
            z.object({
                type: z.literal("DISTINCT_PRODUCT_VALUES"),
                source: z.discriminatedUnion("type", [
                    z.object({
                        type: z.literal("PRODUCT_ATTRIBUTE"),
                        attributeName: z.string(),
                    }),
                    z.object({
                        type: z.literal("CUSTOM_PRODUCT_FIELD"),
                        jsonPath: z.string(),
                    }),
                ]),
            }),
        ]),
    ),
    description: z.string(),
    sortOrder: z.number(),
    editable: z.boolean(),
});

const FieldDisplayConfigSchema = z._default(
    z.object({
        visible: z._default(z.boolean(), false),
        visibleWhenEmpty: z._default(z.boolean(), false),
        editable: z._default(z.boolean(), false),
        labelGerman: z.optional(z.string()),
        labelEnglish: z.optional(z.string()),
        placeholderGerman: z.optional(z.string()),
        placeholderEnglish: z.optional(z.string()),
    }),
    {
        visible: false,
        visibleWhenEmpty: false,
        editable: false,
    },
);

const SharedCustomFieldPropsSchema = z.object({
    key: z.string(),
    editable: z.boolean(),
    visibleWhenEmpty: z.boolean(),
    labelGerman: z.string(),
    labelEnglish: z.string(),
    placeholderGerman: z.optional(z.string()),
    placeholderEnglish: z.optional(z.string()),
    defaultValue: z.optional(
        // Order determines fallback-logic - we try in order until we find a non-null / -undefined / -empty-string value
        z.array(
            z.discriminatedUnion("type", [
                z.object({ type: z.literal("CONSTANT"), value: z.string() }),
                z.object({ type: z.literal("PARTNER_CUSTOM_FIELD"), key: z.string() }),
                z.object({ type: z.literal("CUSTOMER_ACCOUNT_CUSTOM_FIELD"), key: z.string() }),
                z.object({ type: z.literal("DELIVERY_ADDRESS_CUSTOM_FIELD"), key: z.string() }),
                z.object({ type: z.literal("TRANSACTION_CUSTOM_FIELD"), key: z.string() }),
            ]),
        ),
    ),
});

export const CustomFieldConfigSchema = z.discriminatedUnion("type", [
    z.extend(SharedCustomFieldPropsSchema, {
        type: z.literal("text"),
    }),
    z.extend(SharedCustomFieldPropsSchema, {
        type: z.literal("textarea"),
    }),
    z.extend(SharedCustomFieldPropsSchema, {
        type: z.literal("date"),
    }),
    z.extend(SharedCustomFieldPropsSchema, {
        type: z.literal("number"),
        allowDecimals: z.boolean(),
    }),
    z.extend(SharedCustomFieldPropsSchema, {
        type: z.literal("select"),
        innerType: z.enum(["text", "number"]),
        source: z.discriminatedUnion("type", [
            z.object({
                type: z.literal("DICTIONARY"),
                name: z.string(),
            }),
            z.object({
                type: z.literal("SELLING_ENTITY_CODE_DICTIONARY"),
                namesBySellingEntityCode: z.record(z.string(), z.string()),
            }),
            z.object({
                type: z.literal("FIXED"),
                options: z.array(z.string()),
                labels: z.record(z.string(), z.string()),
            }),
        ]),
    }),
]);

export const StandardOrderHeaderFieldSchema = z.enum([
    "orderReference",
    "contactName",
    "contactEmail",
    "contactPhone",
    "orderDate",
    "emailDate",
    "entryDate",
    "totalValue",
    "comment",
    "shippingTerms",
    "paymentTerms",
    "creditLimit",
]);

export const StandardPositionFieldSchema = z.enum([
    "articleNumber1",
    "articleNumber2",
    "articleNumber3",
    "articleNumber4",
    "color",
    "material",
    "length",
    "width",
    "height",
    "dimensions",
    "size",
]);

export const StandardCustomerFieldSchema = z.enum(["name", "address", "partnerNumber", "taxNumber"]);
export const StandardAddressFieldSchema = z.enum(["address"]);

export const PositionFieldConfigSchema = z.object({
    key: z.union([StandardPositionFieldSchema, z.string()]),
    source: z.enum(["PRODUCT", "PRODUCT_CUSTOM_FIELDS", "MATCH_CUSTOM_FIELDS", "NONE"]),
    labelGerman: z.string(),
    labelEnglish: z.string(),
    visibleWhenEmpty: z.boolean(),
    visibleConditionally: z.optional(VisibleConditionallySchema),
    editSettings: z.optional(
        z.object({
            required: z.optional(z.boolean()),
            type: z.enum(["date", "text", "number", "select"]),
            relatedFieldKey: z.optional(z.string()),
            selectSettings: z.optional(
                z.discriminatedUnion("source", [
                    z.object({
                        source: z.literal(["PRODUCT_CUSTOM_FIELDS"]),
                        key: z.string(),
                    }),
                    z.object({
                        source: z.literal("DICTIONARY"),
                        name: z.string(),
                    }),
                ]),
            ),
            offerApplyToAllPositions: z.optional(z.boolean()),
        }),
    ),
});

export const CustomerFieldConfigSchema = z.object({
    key: z.union([StandardCustomerFieldSchema, z.string()]),
    source: z.enum(["BILLING_PARTNER", "BILLING_PARTNER_CUSTOM_FIELDS"]),
    labelGerman: z.string(),
    labelEnglish: z.string(),
    visibleWhenEmpty: z.boolean(),
});

const FieldLabels = z.optional(
    z.object({
        de: z.optional(z.string()),
        en: z.optional(z.string()),
    }),
);

const SOInboxFolderRuleSchema = z.object({
    condition: z.optional(ConditionSchema),
    path: z.string(),
    folderMoveDelayMinutes: z.optional(z.number()),
});

export const SOInboxFolderRulesConfigSchema = z.object({
    type: z.literal("rules"),
    inboxId: z.string(),
    basePath: z._default(z.array(z.string()), []),
    rules: z.array(SOInboxFolderRuleSchema),
    noTransactionFoundFolder: z.optional(z.object({ parentId: z.string(), folderName: z.string() })),
});

export const SOInboxFolderStaticConfigSchema = z.object({
    type: z.pipe(
        z.nullish(z.literal("static")),
        z.transform(x => x ?? "static"),
    ),
    inboxId: z.string(),
    pipelineStartedFolder: z.optional(
        z.object({
            parentId: z.string(),
            folderName: z.string(),
        }),
    ),
    readyForReviewFolder: z.optional(
        z.object({
            parentId: z.string(),
            folderName: z.string(),
        }),
    ),
    submittedFolder: z.optional(
        z.object({
            parentId: z.string(),
            folderName: z.string(),
        }),
    ),
    cancelledFolder: z.optional(
        z.object({
            folderId: z.string(),
        }),
    ),
    noTransactionFoundFolder: z.optional(
        z.object({
            parentId: z.string(),
            folderName: z.string(),
        }),
    ),
    folderMoveDelayMinutes: z._default(z.number(), 5),
});

export const SOInboxFolderConfigSchema = z.discriminatedUnion("type", [
    SOInboxFolderRulesConfigSchema,
    SOInboxFolderStaticConfigSchema,
]);

export const SalesOrderConfigOrderSplitStrategiesSchema = z.enum(["DELIVERY_DATE"]);

export const SalesOrderConfigSchema = z.object({
    autoSubmitEnabled: z._default(z.boolean(), false),
    productTextAttributes: z._default(z.array(z.enum(["title1", "text1", "text2", "text3", "text4", "text5"])), [
        "title1",
    ]),
    extractedPositionFields: z._default(z.array(ExtractedCustomFieldSchema), []),
    extractedHeaderFields: z._default(z.array(ExtractedCustomFieldSchema), []),
    productSelectionFields: z._default(z.array(ExtractedCustomFieldSchema), []),
    priceFetchTriggers: z._default(
        z.object({
            customerSelect: z._default(z.boolean(), true),
            multiMatchSelect: z._default(z.boolean(), true),
            quantityChange: z._default(z.boolean(), true),
            productCardEdit: z._default(z.boolean(), true),
            positionDelete: z._default(z.boolean(), true),
            positionDeleteRevert: z._default(z.boolean(), true),
            uomChange: z._default(z.boolean(), false),
        }),
        {
            customerSelect: true,
            multiMatchSelect: true,
            quantityChange: true,
            productCardEdit: true,
            positionDelete: true,
            positionDeleteRevert: true,
            uomChange: false,
        },
    ),
    orderHeaderFields: z._default(
        z.object({
            comment: FieldDisplayConfigSchema,
            shippingTerms: FieldDisplayConfigSchema,
            paymentTerms: FieldDisplayConfigSchema,
            creditLimit: FieldDisplayConfigSchema,
        }),
        {
            comment: FieldDisplayConfigSchema.parse({}),
            shippingTerms: FieldDisplayConfigSchema.parse({}),
            paymentTerms: FieldDisplayConfigSchema.parse({}),
            creditLimit: FieldDisplayConfigSchema.parse({}),
        },
    ),
    customOrderHeaderFields: z._default(z.array(CustomFieldConfigSchema), []),
    orderHeaderFieldOrder: z._default(z.array(z.union([StandardOrderHeaderFieldSchema, z.string()])), [
        "orderReference",
        "contactName",
        "contactEmail",
        "contactPhone",
        "orderDate",
        "creditLimit",
        "shippingTerms",
        "paymentTerms",
        "comment",
        "totalValue",
    ]),
    positionFieldOrder: z._default(z.array(z.union([StandardPositionFieldSchema, z.string()])), [
        "deliveryDate",
        "quantity",
        "quantityUnit",
        "price",
        "totalValue",
    ]),
    positionFieldAlwaysEditable: z._default(z.array(z.string()), []),
    customerFieldOrder: z._default(z.array(z.union([StandardCustomerFieldSchema, z.string()])), [
        "name",
        "partnerNumber",
        "address",
    ]),
    addressFieldOrder: z._default(z.array(z.union([StandardAddressFieldSchema, z.string()])), ["address"]),
    displayQuantityInProductSalesUnit: z._default(z.boolean(), false),
    handleDeliveryInstructions: z._default(z.boolean(), false),
    deliveryAddressDisabled: z._default(z.boolean(), false),
    allowNewBillingAddressMatch: z._default(z.boolean(), false),
    billingAddressMatchingStrategy: z._default(
        z.enum(["ALWAYS_MATCH", "TAKE_SINGLE_OR_MATCH", "TAKE_DEFAULT_OR_MATCH"]),
        "ALWAYS_MATCH",
    ),
    deliveryAddressMatchingStrategy: z._default(
        z.enum(["ALWAYS_MATCH", "TAKE_SINGLE_OR_MATCH", "TAKE_DEFAULT_OR_MATCH", "TAKE_MAIN_AS_NEW_OR_MATCH"]),
        "ALWAYS_MATCH",
    ),
    deliveryDateDetectionStrategy: z._default(z.enum(["DETECT", "NONE"]), "DETECT"),
    pricePriorities: z._default(
        z.array(z.enum(["CUSTOMER_PRICE_LIST", "GLOBAL_PRICE_LIST", "PRODUCT_PRICE", "EXTRACTED_PRICE"])),
        [],
    ),
    quantityUnitForErpSubmit: z._default(z.enum(["SUBMITTED", "DEFAULT_SALES"]), "SUBMITTED"),
    productSalesUnitRoundingStrategy: z.optional(z.enum(["COMMERCIALLY", "ROUND_UP", "ROUND_DOWN"])),
    paymentTermsWhenAboveCreditLimit: z._default(z.nullable(z.string()), null),
    replyTemplate: z._default(z.nullable(z.string()), null),
    positionFields: z._default(z.array(PositionFieldConfigSchema), defaultPositionFields),
    customerFields: z._default(z.array(CustomerFieldConfigSchema), defaultCustomerFields),
    billingAddressLabel: z._default(
        z.object({
            DE: z._default(z.string(), "Rechnung"),
            EN: z._default(z.string(), "Billing"),
        }),
        {
            DE: "Rechnung",
            EN: "Billing",
        },
    ),
    standardBillingAddressLabel: z._default(
        z.object({
            DE: z._default(z.string(), "Standard Rechnung"),
            EN: z._default(z.string(), "Default billing"),
        }),
        {
            DE: "Standard Rechnung",
            EN: "Default billing",
        },
    ),
    standardDeliveryAddressLabel: z._default(
        z.object({
            DE: z._default(z.string(), "Standard Lieferung"),
            EN: z._default(z.string(), "Default shipping"),
        }),
        {
            DE: "Standard Lieferung",
            EN: "Default shipping",
        },
    ),
    inboxFolders: z._default(z.array(SOInboxFolderConfigSchema), []),
    inboxLabelsGerman: z._default(SalesOrderConfigInboxLabels, {}),
    inboxLabelsEnglish: z._default(SalesOrderConfigInboxLabels, {}),
    addedBusinessDaysWhenImmediateDeliveryRequested: z._default(z.number(), 1),
    emailPushCondition: z._default(z.enum(["ALWAYS", "NEVER", "ONLY_WHEN_NO_ATTACHMENTS"]), "ONLY_WHEN_NO_ATTACHMENTS"),
    emailPushFileName: z.optional(z.string()),
    salesOrderBasic: z._default(
        z.object({
            productChoiceBase: z._default(z.enum(["ALL_PRODUCTS", "CUSTOMER_PRODUCTS"]), "ALL_PRODUCTS"),
            stripProductTexts: z._default(z.boolean(), false),
            allowNoMatch: z._default(z.boolean(), true),
            allowMultiMatch: z._default(z.boolean(), true),
        }),
        {
            productChoiceBase: "ALL_PRODUCTS",
            stripProductTexts: false,
            allowNoMatch: true,
            allowMultiMatch: true,
        },
    ),
    salesOrderPOC: z._default(
        z.object({
            showProductCustomFieldsToLLM: z._default(z.boolean(), false),
            showUnitPricesToLLM: z.optional(z.array(z.string())),
            defaultCustomerAccountIdForPriceLookup: z.optional(z.string()),
            validateMultiMatchesWithThoughts: z._default(z.boolean(), false),
            minimumLengthForLevenshteinDiff: z.optional(z.number()),
            allowedLevenshteinDiffForArticleNumber: z.optional(z.number()),
            allowedArticleNumberPrefixLength: z.optional(z.number()),
            allowedArticleNumberSuffixLength: z.optional(z.number()),
        }),
        {
            showProductCustomFieldsToLLM: false,
            validateMultiMatchesWithThoughts: false,
        },
    ),
    productSearchDefaultSuggestions: z.optional(z.enum(["CUSTOMER_PRODUCTS"])),
    contactMatchingEnabled: z._default(z.boolean(), false),
    replyGenerationEnabled: z._default(z.boolean(), false),
    attachedImageByteSizeThreshold: z._default(z.number(), 50_000),
    orderDateStrategy: z._default(z.enum(["DOCUMENT_DATE", "EMAIL_DATE", "TODAY"]), "DOCUMENT_DATE"),
    sellingEntityHeader: z._default(
        z.object({
            DE: z._default(z.string(), "Verkaufsorganisation"),
            EN: z._default(z.string(), "Selling Organisation"),
        }),
        {
            DE: "Verkaufsorganisation",
            EN: "Selling Organisation",
        },
    ),
    sellingEntityFields: z._default(z.array(z.enum(["name", "address"])), ["name", "address"]),
    sellingEntityEditable: z._default(z.enum(["NO", "INLINE"]), "NO"),
    mockSubmission: z._default(z.boolean(), false),
    requiredFieldsForSubmission: z._default(z.array(z.enum(["deliveryDate"])), []),
    productIdWhenNoMatch: z.optional(z.string()),
    productIdWhenNoMatchBadgeText: z.optional(z.string()),
    productInvalidLabel: z._default(
        z.nullable(
            z.object({
                DE: z._default(z.string(), "Deaktiviert"),
                EN: z._default(z.string(), "Disabled"),
            }),
        ),
        {
            DE: "Deaktiviert",
            EN: "Disabled",
        },
    ),
    standardHeaderFieldLabels: z.optional(
        z.object({
            orderReference: FieldLabels,
            quoteNumber: FieldLabels,
            totalValue: FieldLabels,
        }),
    ),
    standardPositionFieldLabels: z.optional(
        z.object({
            price: FieldLabels,
            totalValue: FieldLabels,
        }),
    ),
    orderSplitStrategy: z.optional(
        z.object({
            strategy: SalesOrderConfigOrderSplitStrategiesSchema,
            applyByDefault: z.boolean(),
            offerReprocess: z.boolean(),
        }),
    ),
    ignoreGlobalPriceLists: z._default(z.boolean(), false),
    nonExactArticleNumberMatch: z._default(z.boolean(), true),
    partialCustomerArticleNumberMatch: z._default(z.boolean(), false),
    matchCustomerArticleNumbersGlobally: z._default(z.boolean(), false),
    exposedDictionariesToUI: z.optional(z.array(z.string())),
    judgeTransactionDetection: z._default(z.boolean(), true),
    allowOnlyConvertableToUoMsOnPositionEditing: z._default(z.boolean(), false),
    searchProductsByCustomerAccountNumbers: z._default(z.boolean(), false),
    searchProductsByTextSearch: z._default(z.boolean(), false),
    restrictSearchToCustomerProducts: z._default(z.boolean(), false),
    additionalCostsEnabled: z._default(z.boolean(), false),
    priceFetchEnabled: z._default(z.boolean(), true),
    additionalOrderNumberSeparator: z.optional(z.string()),
    additionalOrderNumberOnlyForPartnerNumbers: z.optional(z.array(z.string())),
    warnings: z._default(z.array(z.enum(["QUOTE_NUMBER"])), []),
    eliminateQuantityZeroPositions: z._default(z.boolean(), false),
    allowedClassifications: z.optional(z.array(ClassificationSchema)),
    classifications: z.optional(z.array(ClassificationConfigEntrySchema)),
});

export const CustomerConfigSchema = z.object({
    orgId: OrganizationIdSchema,
    name: z.string(),
    slug: z.string(),
    goLiveDate: z.nullable(z.string()),
    domain: z.string(),
    erpSystem: ErpSystemSchema,
    documentSystem: z.nullable(DocumentSystemSchema),
    emailSystem: z.nullable(EmailSystemSchema),
    syncEnabled: z.boolean(),
    authSecretsPath: z.string(),
    authSecretsPathByInboxId: z.nullable(z.record(InboxIdSchema, z.string())),
    documentSystemAuthSecretsPath: z.nullable(z.string()),
    emailSystemAuthSecretsPath: z.nullable(z.string()),
    config: SalesOrderConfigSchema,
    legalEntityNames: z.array(z.string()),
    defaultCurrencyCode: z.string(),
    defaultLocale: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export const MasterDataSourceSchema = z.enum(masterDataSources);

const CsvBaseConfigSchema = z.object({
    delimiter: z.optional(z.string()),
    skipLines: z.optional(z.number()),
    escape: z.nullish(z.union([z.string(), z.boolean()])),
    quote: z.nullish(z.union([z.string(), z.boolean()])),
    relaxQuotes: z.nullish(z.boolean()),
    relaxColumnCountMore: z.optional(z.boolean()),
    dataSetId: z.optional(z.string()),
});

export const CsvFromS3ConfigSchema = z.extend(
    z.object({
        configType: z.literal("CSV_FROM_S3"),
        bucket: z.string(),
        folder: z.string(),
        key: z.nullable(
            z.discriminatedUnion("type", [
                z.object({
                    type: z.literal("EXACT"),
                    key: z.string(),
                }),
                z.object({
                    type: z.literal("TIMESTAMPED"),
                    timestampedKeyPattern: z.string(),
                    prefix: z.optional(z.string()),
                    compareBy: z.optional(z.enum(["KEY", "LAST_MODIFIED"])),
                }),
            ]),
        ),
        preprocessorId: z.optional(z.string()),
        preprocessorConfig: z.optional(SerializableSchema),
    }),
    CsvBaseConfigSchema.shape,
);

export const CsvFromSftpConfigSchema = z.extend(
    z.object({
        configType: z.literal("CSV_FROM_SFTP"),
        authSecretPath: z.string(),
        folder: z.optional(z.string()),
        key: z.string(),
    }),
    CsvBaseConfigSchema.shape,
);

export const OdooWithSecretConfigSchema = z.object({
    configType: z.literal("ODOO"),
    authSecretPath: z.string(),
});

export const SapB1WithSecretConfigSchema = z.object({
    configType: z.literal("SAP_B1"),
    authSecretPath: z.string(),
});

export const NetsuiteWithSecretConfigSchema = z.object({
    configType: z.literal("NETSUITE"),
    authSecretPath: z.string(),
});

export const ReflexPIMWithSecretConfigSchema = z.object({
    configType: z.literal("REFLEX_PIM"),
    authSecretPath: z.string(),
});

export const EpicorWithSecretConfigSchema = z.object({
    configType: z.literal("EPICOR"),
    authSecretPath: z.string(),
});

export const OrderwiseWithSecretConfigSchema = z.object({
    configType: z.literal("ORDERWISE"),
    authSecretPath: z.string(),
});

export const MSSQLWithSecretConfigSchema = z.object({
    configType: z.literal("MSSQL"),
    authSecretPath: z.string(),
});

export const HaufeX360WithSecretConfigSchema = z.object({
    configType: z.literal("HAUFE_X360"),
    authSecretPath: z.string(),
});

export const SucoConfigSchema = z.object({
    configType: z.literal("SUCO"),
    authSecretPath: z.string(),
});

export const GehrConfigSchema = z.object({
    configType: z.literal("GEHR"),
    authSecretPath: z.string(),
});

export const UtzConfigSchema = z.object({
    configType: z.literal("UTZ"),
    authSecretPath: z.string(),
});

const S3KeySchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("EXACT"),
        key: z.string(),
    }),
    z.object({
        type: z.literal("TIMESTAMPED"),
        timestampedKeyPattern: z.string(),
        prefix: z.optional(z.string()),
        compareBy: z.optional(z.enum(["KEY", "LAST_MODIFIED"])),
    }),
]);

export const WikaConfigSchema = z.object({
    configType: z.literal("WIKA"),
    bucket: z.string(),
    folder: z.string(),
    itemsKey: S3KeySchema,
    modelCodeKey: S3KeySchema,
    dataSetId: z.optional(z.string()),
});

export const MasterDataSyncConfigSchema = z.discriminatedUnion("configType", [
    OdooWithSecretConfigSchema,
    SapB1WithSecretConfigSchema,
    CsvFromS3ConfigSchema,
    CsvFromSftpConfigSchema,
    ReflexPIMWithSecretConfigSchema,
    NetsuiteWithSecretConfigSchema,
    EpicorWithSecretConfigSchema,
    OrderwiseWithSecretConfigSchema,
    MSSQLWithSecretConfigSchema,
    HaufeX360WithSecretConfigSchema,
    SucoConfigSchema,
    GehrConfigSchema,
    UtzConfigSchema,
    WikaConfigSchema,
]);

export const MasterDataSourceEntrySchema = z.object({
    orgId: z.string(),
    entityType: z.union([EntityTypeSchema, z.enum([""])]),
    source: MasterDataSourceSchema,
    dataSetId: z.string(),
    active: z.boolean(),
    config: MasterDataSyncConfigSchema,
    executionOrder: z.nullable(z.number()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
